// src/components/ItemsPrinter.js

import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import './ItemsPrinter.css';

export default function ItemsPrinter() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const preRef = useRef(null);

  useEffect(() => {
    async function loadItems() {
      try {
        const resp = await api.get('/items');
        setItems(resp.data);
      } catch (err) {
        console.error('Error fetching items:', err);
        alert('Failed to load items for printing.');
      } finally {
        setLoading(false);
      }
    }
    loadItems();
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', marginTop: '20px' }}>Loading…</div>;
  }

  // Helpers to pad fields for fixed-width columns
  // We assume a total line length of 32 characters:
  //   • Item name column: 20 chars max  
  //   • Qty column (not needed here, but if you want): 3 chars  
  //   • Price column: 7 chars (including ₹ and decimals)  
  //   • Spaces between: adjust so total is 32  
  const padName = (s) => {
    const maxLen = 20;
    if (s.length > maxLen) return s.slice(0, maxLen - 3) + '...';
    return s.padEnd(maxLen, ' ');
  };
  const padPrice = (p) => {
    // Format to two decimals, prefix with ₹
    const str = `₹${parseFloat(p).toFixed(2)}`;
    // Price column width = 8 chars; right-align
    return str.padStart(8, ' ');
  };

  // Build <pre> contents: header + each item + total count
  const nowStr = new Date().toLocaleString();
  const headerLine = `${padName('Item Name')}    ${padPrice('Price')}`; // 4 spaces between columns
  let rows = [headerLine];
  rows.push('-'.repeat(32));

  items.forEach((item) => {
    const lineName = padName(item.Name);
    const priceNum = isNaN(parseFloat(item.Price)) ? 0.0 : parseFloat(item.Price);
    const linePrice = padPrice(priceNum);
    rows.push(`${lineName}    ${linePrice}`);
  });

  rows.push('-'.repeat(32));
  const totalLine = `Total Items: ${items.length.toString().padStart(2, ' ')}`
    .padEnd(32, ' ');
  rows.push(totalLine);

  const preContent = rows.join('\n');

  // When “Print Items” is clicked, open new window and inject only the <pre> + inline CSS
  const handlePrint = () => {
    if (!preRef.current) return;

    // Inline CSS tailored for 2" (≈58 mm) printer at 203 dpi (≈460 px)
    const inlineStyle = `
      <style>
        @page {
          size: 2in auto;
          margin: 0;
        }
        body {
          margin: 0;
          padding: 4px;
          width: 460px;            /* ~58 mm at 203 dpi */
          font-family: "Courier New", Courier, monospace;
          font-size: 10px;
          line-height: 1.2;
          color: #000;
        }
        .header {
          text-align: center;
          font-size: 12px;
          margin-bottom: 4px;
        }
        .title {
          text-align: center;
          font-size: 12px;
          margin: 4px 0;
        }
        pre {
          white-space: pre;
          font-family: monospace;
          margin: 0;
        }
        .footer {
          text-align: center;
          font-size: 9px;
          margin-top: 6px;
        }
      </style>
    `;

    // Build print HTML
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8"/>
          <title>Print Items</title>
          ${inlineStyle}
        </head>
        <body>
          <div class="header">${nowStr}</div>
          <div class="title">Item List</div>
          <pre>${preContent}</pre>
          <div class="footer">All right reserved</div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 300);
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      alert('Please allow popups to print.');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  return (
    <div style={{ textAlign: 'center', padding: '16px' }}>
      <button
        onClick={handlePrint}
        style={{
          padding: '8px 16px',
          fontSize: '14px',
          cursor: 'pointer',
          marginBottom: '12px',
        }}
      >
        Print Items
      </button>

      {/* On-screen preview (optional) */}
      <div className="items-preview-container">
        <div className="header">{nowStr}</div>
        <div className="title">Item List</div>
        <pre ref={preRef} className="preformatted">
          {preContent}
        </pre>
        <div className="footer">All right reserved</div>
      </div>
    </div>
  );
}
